import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { requestAdminOtp, verifyAdminOtp } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { AuthCard } from './auth-card';

export function OtpPage() {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(30);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const verify = useMutation({
    mutationFn: () => verifyAdminOtp(email, code),
    onSuccess: (data) => {
      // The admin endpoint already guarantees role=ADMIN server-side and
      // refuses non-admin emails. No client-side role check needed.
      getTokenStore().setAccessToken(data.accessToken, 'admin');
      qc.clear();
      setSession(data.user);
      navigate('/transactions', { replace: true });
    },
  });

  const resend = useMutation({
    mutationFn: () => requestAdminOtp(email),
    // Reset on any outcome — failed resend shouldn't leave the button hot.
    onSettled: () => setSecondsLeft(30),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6 || !email) return;
    verify.mutate();
  }

  return (
    <AuthCard>
      <div className="space-y-5">
        <div className="text-center">
          <h1 className="text-lg font-semibold text-gray-900">Verify your email</h1>
          <p className="mt-1 text-sm text-gray-600">
            We sent a code to <span className="font-semibold">{email}</span>.
          </p>
          <Link
            to="/login"
            className="mt-1 inline-block text-xs text-gray-600 underline underline-offset-2"
          >
            Not you?
          </Link>
        </div>

        <form className="space-y-3" onSubmit={onSubmit}>
          <Input
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="Enter 6 digit OTP"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
          />
          <div className="text-xs text-gray-500">
            Didn&rsquo;t get it?{' '}
            {secondsLeft > 0 ? (
              <span>Resend OTP (in {secondsLeft}s)</span>
            ) : (
              <button
                type="button"
                className="underline underline-offset-2 disabled:opacity-50"
                onClick={() => resend.mutate()}
                disabled={resend.isPending}
              >
                {resend.isPending ? 'Resending…' : 'Resend OTP'}
              </button>
            )}
          </div>
          {verify.isError ? (
            <p className="text-sm text-red-600">
              {apiErrorMessage(verify.error, 'Invalid or expired OTP')}
            </p>
          ) : null}
          <Button type="submit" block disabled={code.length !== 6 || verify.isPending}>
            {verify.isPending ? 'Verifying…' : 'Continue'}
          </Button>
        </form>
      </div>
    </AuthCard>
  );
}
